<template>
	<q-page padding>
		<div class="q-display-1 q-mb-md text-secondary">
			<span>
				<q-icon name="far fa-books" class="q-mb-sm" /> {{ $t(title_library) }}

				<q-tooltip anchor="bottom right" self="top left">
					{{ $t('library_help') }}
				</q-tooltip>
			</span>
		</div>

		<filters
			class="filter-card"
			:options="getOptionsFilter"
			@requestFiltered="requestFiltered"
			@clearAndRequest="clearAndRequest"
		/>

		<q-table
			:data="categories"
			:columns="columns"
			:pagination.sync="server_pagination"
			@request="request"
			:loading="loading"
			:filter="filter"
			row-key="name"
			:no-data-label="$t('no_data_avaliable')"
			:no-results-label="$t('no_results_found')"
			:loading-label="$t('loading_data')"
			:rows-per-page-label="$t('items_per_page')"
			:rows-per-page-options="[5, 10, 50, 100]"
			binary-state-sort
		>
			<template slot="top" slot-scope="props" :props="props">
				<q-search
					v-model="filter"
					icon="far fa-search"
					:placeholder="$t('search')"
					:debounce="500"
				/>

				<div class="col" />

				<!-- <q-btn-dropdown color="secondary" :label="$t('reports_bi')" class="q-mr-sm" ref="dropdownReport" v-if="report_list.length > 0 && reportsPermission">
					<q-list highlight separator link>
						<q-item
							v-for="(report, index) in report_list"
							:key="`i${index}`"
							@click.native="showReportBI(report)">
							<q-item-main>
								{{ $t(report.slug) }}
							</q-item-main>
						</q-item>
					</q-list>
				</q-btn-dropdown> -->

				<q-btn
					color="secondary"
					v-if="reportsPermission"
					class="q-mr-sm"
					icon="fas fa-download"
					:disable="$buttonDelay('libraries_report_button_delay')"
					:label="$t('library_report')"
					@click="downloadReport()"
				>
					<q-tooltip
						v-if="$buttonDelay('libraries_report_button_delay')"
						anchor="top middle"
						self="bottom middle"
						:offset="[10, 10]"
					>
						{{ $t('report_button_delay') }}
					</q-tooltip>
				</q-btn>
				<q-btn
					color="secondary"
					icon="far fa-plus"
					:label="$t('new')"
					@click="$router.push({ name: 'admin_library', params: { id: 0 } })"
				/>
			</template>

			<q-tr slot="header" slot-scope="props" :props="props">
				<q-th v-for="col in props.cols" :key="col.name" :props="props">
					{{ $t(col.label) }}
				</q-th>
			</q-tr>

			<q-tr slot="body" slot-scope="props" :props="props">
				<q-td auto-width key="id" :props="props">
					{{ props.row.id }}
				</q-td>

				<q-td key="title" :props="props">
					{{ props.row.title }}
				</q-td>

				<q-td key="permission" :props="props">
					<q-btn
						color="primary"
						text-color="white"
						:label="getQuantity(props.row.permission)"
						no-ripple
					>
						<q-tooltip>
							<p
								v-if="props.row.permission.list_areas.length > 0"
								class="tooltip q-mb-sm"
							>
								{{ $t('areas') }}: {{ props.row.permission.list_areas.join(', ') }}
							</p>

							<p
								v-if="props.row.permission.list_profiles.length > 0"
								class="tooltip q-mb-sm"
							>
								{{ $t('profile') }}:
								{{
									props.row.permission.list_profiles.map((p) => p.name).join(', ')
								}}
							</p>

							<p
								v-if="props.row.permission.list_user_groups.length > 0"
								class="tooltip q-mb-sm"
							>
								{{ $t('user_groups') }}:
								{{
									props.row.permission.list_user_groups
										.map((ug) => ug.name)
										.join(', ')
								}}
							</p>

							<p
								v-if="props.row.permission.list_tags.length > 0"
								class="tooltip q-mb-sm"
							>
								{{ $t('tags') }}:
								{{ props.row.permission.list_tags.map((t) => t.name).join(', ') }}
							</p>
						</q-tooltip>
					</q-btn>
				</q-td>

				<q-td key="extension" :props="props">
					{{ props.row.extension }}
				</q-td>

				<q-td key="library_file_category" :props="props">
					{{ getCategory(props.row.category) }}
				</q-td>

				<q-td key="hits" :props="props">
					{{ props.row.hits }}
				</q-td>

				<q-td key="views" :props="props">
					{{ props.row.views }}
				</q-td>

				<q-td key="created_date" :props="props">
					{{ props.row.created_date }}
				</q-td>

				<q-td key="status" :props="props" auto-width>
					<q-chip
						small
						:icon="getIconStatus(props.row.status)"
						:color="getColorStatus(props.row.status)"
						class="full-width"
					>
						{{ $t(getStatus(props.row.status)) }}
					</q-chip>
				</q-td>

				<q-td key="actions" :props="props">
					<q-btn
						flat
						round
						color="primary"
						icon="far fa-pencil-alt"
						@click="
							$router.push({
								name: 'admin_library',
								params: { id: props.row.actions },
							})
						"
					>
						<q-tooltip>
							{{ $t('view_or_edit_tooltip') }}
						</q-tooltip>
					</q-btn>

					<q-btn
						flat
						round
						color="secondary"
						icon="far fa-chart-line"
						@click="showLibraryReport(props.row)"
					>
						<q-tooltip>
							{{ $t('report_file_downloads_tooltip') }}
						</q-tooltip>
					</q-btn>

					<q-btn
						flat
						round
						color="negative"
						icon="far fa-trash-alt"
						@click="confirmRemove(props.row.actions)"
					>
						<q-tooltip>
							{{ $t('delete_tooltip') }}
						</q-tooltip>
					</q-btn>
				</q-td>
			</q-tr>
		</q-table>

		<q-dialog v-model="confirm_remove" @ok="removeItem">
			<span slot="title">{{ $t('remove_item') }}</span>

			<span slot="message">{{ $t('confirm_remove_item') }}</span>

			<template slot="buttons" slot-scope="props" :props="props">
				<q-btn color="primary" :label="$t('cancel')" @click="confirm_remove = false" />
				<q-btn color="negative" :label="$t('remove')" @click="removeItem()" />
			</template>
		</q-dialog>

		<q-modal
			v-model="show_report"
			:content-css="{ minWidth: '100vw', maxWidth: '100vw', minHeight: '100vh' }"
		>
			<q-modal-layout>
				<q-toolbar slot="header">
					<q-btn flat round dense @click="show_report = false" icon="fas fa-times" />
					<q-toolbar-title>
						{{ $t('reports') }}
					</q-toolbar-title>
				</q-toolbar>

				<div class="layout-padding">
					<div style="position:relative; width:100%; height:0px; padding-bottom:56.25%;">
						<iframe
							style="position:absolute; left:0; top:0; width:100%; height:100%"
							:src="report_url"
							frameborder="0"
						>
						</iframe>
					</div>
				</div>
			</q-modal-layout>
		</q-modal>

		<q-modal
			v-model="show_report"
			:content-css="{ minWidth: '100vw', maxWidth: '100vw', minHeight: '100vh' }"
		>
			<q-modal-layout>
				<q-toolbar slot="header">
					<q-btn flat round dense @click="show_report = false" icon="fas fa-times" />
					<q-toolbar-title>
						{{ $t('reports') }}
					</q-toolbar-title>
				</q-toolbar>

				<div class="layout-padding">
					<div style="position:relative; width:100%; height:0px; padding-bottom:56.25%;">
						<iframe
							style="position:absolute; left:0; top:0; width:100%; height:100%"
							:src="report_url"
							frameborder="0"
						>
						</iframe>
					</div>
				</div>
			</q-modal-layout>
		</q-modal>

		<q-dialog v-model="show_report_problem" prevent-close>
			<span slot="title">{{ $t('report_download_failed') }}</span>

			<div slot="message">
				<span class="q-pa-xs">{{ $t('report_download_failed_exceeded_time') }}</span>
				<p class="q-pa-xs">{{ $t('the_report_it_will_be_sent_to_your_email') }}</p>
			</div>

			<template slot="buttons" slot-scope="props" :props="props" class="justify-center">
				<div class="row full-width q-mb-xs">
					<q-btn
						color="positive"
						class="offset-4 col-4"
						:label="$t('ok')"
						@click="show_report_problem = false"
					/>
				</div>
			</template>
		</q-dialog>

		<reports
			:show_report_library="show_report_library"
			:id="action_id"
			:title="action_title"
			@hide_report="hideReport()"
		/>
	</q-page>
</template>

<script>
import { mapGetters } from 'vuex'
import Reports from 'components/admin/library/Report.vue'
import Filters from 'components/admin/library/Filters.vue'
import { LocalStorage } from 'quasar'
import { date } from 'quasar'
import * as moment from 'moment'
import * as tz from 'moment-timezone'
export default {
	i18nOptions: { namespaces: ['custom', 'common', 'library', 'admin', 'imports'] },

	components: {
		reports: Reports,
		filters: Filters,
	},

	data() {
		return {
			server_pagination: {
				page: 1,
				rowsNumber: 10,
				rowsPerPage: null,
				descending: false,
				sortBy: null,
			},
			loading: false,
			categories: [],
			columns: [
				{
					name: 'id',
					required: true,
					label: '#',
					align: 'left',
					field: 'name',
					sortable: true,
				},
				{
					name: 'title',
					required: true,
					label: 'file',
					align: 'left',
					field: 'title',
					sortable: true,
				},
				{
					name: 'permission',
					required: true,
					label: 'permission',
					align: 'left',
					field: 'permission',
					sortable: false,
				},
				{
					name: 'extension',
					required: true,
					label: 'type',
					align: 'left',
					field: 'extension',
					sortable: true,
				},
				{
					name: 'library_file_category',
					required: true,
					label: 'category',
					align: 'left',
					field: 'library_file_category',
					sortable: true,
				},
				{
					name: 'hits',
					required: true,
					label: 'downloads',
					align: 'left',
					field: 'hits',
					sortable: true,
				},
				{
					name: 'views',
					required: true,
					label: 'views',
					align: 'left',
					field: 'views',
					sortable: true,
				},
				{
					name: 'created_date',
					required: true,
					label: 'created_date',
					align: 'left',
					field: 'created_date',
					sortable: true,
					show: true,
				},
				{
					name: 'status',
					required: true,
					label: 'status',
					align: 'center',
					field: 'status',
					sortable: false,
				},
				{
					name: 'actions',
					required: true,
					label: '',
					align: 'right',
					field: 'edit',
					sortable: false,
				},
			],
			filter: '',
			action_id: '',
			action_title: '',
			confirm_remove: false,
			show_report: false,
			report_url: '',
			dropdown_reports: false,
			show_report_library: false,
			show_report_problem: false,
			filter_areas: [],
			filter_profile: [],
			filter_category: [],
			filter_status: [],
			filter_extension: [],
			filter_start_date: [],
			filter_end_date: [],
			report_list: [],
		}
	},

	computed: {
		...mapGetters({
			access_token: 'common/access_token',
			areas: 'registration_lists/country_areas',
			categories_list: 'registration_lists/library_file_categories',
			clients: 'params/clients',
			id_user_client_selected: 'common/id_user_client_selected',
			library_file_categories: 'registration_lists/library_file_categories',
			library_file_extensions: 'registration_lists/library_file_extensions',
			num_rows: 'datatable/num_rows',
			profiles: 'registration_lists/profiles',
			reports: 'registration_lists/reports',
			user_client_module_items: 'common/user_client_module_items',
			library_filters: 'admin_filters/library_filters',
			reports_store: 'registration_lists/reports',
		}),

		getOptionsFilter() {
			return {
				areas: this.areas ? this.areas : [],
				profiles: this.profiles ? this.profiles : [],
				library_file_categories: this.library_file_categories
					? this.library_file_categories
					: [],
				library_file_extensions: this.library_file_extensions
					? this.library_file_extensions
					: [],
				statuses: [
					{ value: 1, label: this.$t('published') },
					{ value: 2, label: this.$t('unpublished') },
				],
			}
		},

		getHeader() {
			let headers = {}
			return (headers = {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
					authorization: 'Bearer ' + this.access_token,
				},
			})
		},

		title_library() {
			if (this.id_user_client_selected) {
				let obj = this.clients.find((obj) => {
					return obj.id === this.id_user_client_selected
				})
				if (obj) {
					return obj.title_library == '' ? 'library' : obj.title_library
				}
			}
		},

		filteredReports() {
			let obj = this.reports.filter((obj) => {
				return (
					obj.slug_type === 'library_files' &&
					obj.url != 'https://www.take5.com.br' &&
					obj.url != ''
				)
			})
			return obj
		},

		hasReports() {
			let obj = this.user_client_module_items.find((obj) => {
				return obj.id === this.id_user_client_selected
			})
			if (obj) {
				let item = obj.module_items.find((obj) => {
					return obj.slug === 'reports_panel'
				})
				if (item) {
					return item.permission
				}
			}
			return false
		},

		reportsPermission() {
			let obj = this.user_client_module_items.find((obj) => {
				return obj.id === this.id_user_client_selected
			})
			if (obj) {
				let item = obj.module_items.find((obj) => {
					return obj.slug === 'reports'
				})
				if (item) {
					return item.permission
				}
			}
			return false
		},
		haveFilter() {
			for (const property in this.library_filters) {
				if (this.library_filters[property].length > 0) return true
			}
			return false
		},
	},

	methods: {
		fetchLists() {
			this.$q.loading.show()

			this.$axios
				.get('/accounts/get_registration_lists/', this.getHeader)
				.then((response) => {
					this.$store.dispatch('registration_lists/SET_LISTS', [response.data])
					this.$q.loading.hide()

					if (this.haveFilter) {
						this.requestFiltered()
					} else {
						this.request({
							pagination: this.server_pagination,
						})
					}
				})
				.catch((e) => {
					this.$q.loading.hide()
					console.log(e)
				})
		},

		request({ pagination }) {
			this.loading = true
			this.server_pagination = pagination

			let order = pagination.descending ? '-' : ''
			let order_by = pagination.sortBy ? order + pagination.sortBy : ''

			let data = {
				page_size: this.server_pagination.rowsPerPage,
				page: this.server_pagination.page,
				search: this.filter,
				areas__id__in: this.filter_areas.join(','),
				profiles__id__in: this.filter_profile.join(','),
				library_file_category__id__in: this.filter_category.join(','),
				extension__in: this.filter_extension.join(','),
				created_date__gt: this.filter_start_date,
				created_date__lt: this.filter_end_date,
				status__in: this.filter_status.join(','),
				ordering: order_by,
			}

			this.savePagination()

			this.$axios
				.get(
					`/admin_library_files/files/?${this.$qs.stringify(data, {
						encode: false,
						indices: false,
						arrayFormat: 'comma',
					})}`,
					this.getHeader
				)
				.then((response) => {
					this.server_pagination.rowsNumber = response.data.count

					this.categories = []
					response.data.results.forEach((result) => {
						this.categories.push({
							id: result.id,
							title: result.title,
							extension: result.extension,
							category: result.library_file_category,
							hits: result.hits,
							views: result.views,
							permission: {
								list_tags: result.tags,
								list_user_groups: result.user_groups,
								list_profiles: result.profiles,
								list_areas: result.areas.map((e) => e.name),
							},
							status: result.status,
							created_date: date.formatDate(result.created_date, 'DD/MM/YYYY HH:mm'),
							actions: result.id,
						})
					})

					this.loading = false
				})
				.catch((e) => {
					this.loading = false
					console.log(e)
				})
		},

		requestFiltered() {
			this.filter_areas = this.library_filters.area
			this.filter_profile = this.library_filters.profile
			this.filter_status = this.library_filters.status
			this.filter_category = this.library_filters.category
			this.filter_extension = this.library_filters.extension
			this.filter_start_date = [
				date.formatDate(this.library_filters.start_date_display, 'YYYY-MM-DD HH:mm:ss'),
			]
			this.filter_end_date = [
				date.formatDate(this.library_filters.end_date_display, 'YYYY-MM-DD HH:mm:ss'),
			]

			this.request({
				pagination: {
					page: 1,
					rowsNumber: 10,
					rowsPerPage: this.num_rows,
					descending: false,
					sortBy: null,
				},
			})
		},

		clearAndRequest() {
			this.filter_area = []
			this.filter_profile = []
			this.filter_category = []
			this.filter_status = []
			this.filter_extension = []
			this.filter_start_date = []
			this.filter_end_date = []

			this.request({
				pagination: {
					page: 1,
					rowsNumber: 10,
					rowsPerPage: this.num_rows,
					descending: false,
					sortBy: null,
				},
			})
		},

		confirmRemove(id) {
			this.action_id = id
			this.confirm_remove = true
		},

		removeItem() {
			this.$q.loading.show()

			this.$axios
				.delete(`/admin_library_files/files/${this.action_id}/`, this.getHeader)
				.then((response) => {
					this.$q.loading.hide()

					this.request({
						pagination: this.server_pagination,
					})

					this.confirm_remove = false

					this.$q.notify({
						message: this.$t('item_removed_success'),
						type: 'positive',
						icon: 'far fa-trash-alt',
					})
				})
				.catch((e) => {
					this.$q.loading.hide()
					console.log(e)
				})
		},

		getFeatured(featured) {
			if (featured == 1) return 'yes'
			else return 'no'
		},

		getStatus(status) {
			if (status == 1) return 'published'
			else if (status == 2) return 'unpublished'
			else if (status == 3) return 'deleted'
		},

		getIconStatus(status) {
			if (status == 1) return 'fas fa-check'
			else if (status == 2) return 'fas fa-ban'
			else if (status == 3) return 'fas fa-trash-alt'
		},

		getColorStatus(status) {
			if (status == 1) return 'positive'
			else if (status == 2) return 'warning'
			else if (status == 3) return 'negative'
		},

		getCategory(id) {
			let category = this.categories_list.find((obj) => {
				return obj.value === id
			})
			if (category) return category.label
			else return ''
		},

		showReport(report) {
			this.report_url = report.url
			this.dropdown_reports = false
			this.show_report = true
		},

		hideReport() {
			this.action_id = ''
			this.show_report_library = false
			this.loadPagination()
		},

		showLibraryReport(training) {
			this.action_id = training.id
			this.action_title = training.title
			this.show_report_library = true
		},

		savePagination() {
			this.$store.dispatch('datatable/SET_NUM_ROWS', [this.server_pagination.rowsPerPage])
		},

		loadPagination() {
			this.server_pagination.rowsPerPage = this.num_rows
		},

		getQuantity(permission) {
			return (
				permission.list_tags.length +
				permission.list_user_groups.length +
				permission.list_profiles.length +
				permission.list_areas.length
			)
		},

		downloadReport() {
			if (this.$buttonDelay('libraries_report_button_delay')) return
			let message = this.$t('generating_import_file_message')
			this.$q.loading.show({
				message,
			})
			this.$axios
				.get(`admin_library_files/files/generate_report/`, this.getHeader)
				.then((response) => {
					this.forceFileDownload(response.data.path_url)
				})
				.catch((e) => {
					this.loading = false
					this.show_report_problem = true
					console.log(e)
				})
				.finally(() => {
					this.$store.dispatch('common/SET_LIBRARIES_REPORT_BUTTON_DELAY', [new Date()])
					this.$q.loading.hide()
				})
		},

		forceFileDownload(response) {
			const link = document.createElement('a')
			link.href = response
			document.body.appendChild(link)
			link.click()
		},

		loadReports() {
			if (this.reports_store)
				this.report_list = this.reports_store.filter((obj) => {
					return (
						obj.slug_type === 'library_files' &&
						obj.url != 'https://www.take5.com.br' &&
						obj.url != ''
					)
				})
		},

		getAuthParamsData(url_original) {
			this.$q.loading.show()
			this.$axios
				.get('/reports/reports/get_looker_studio_challenge_token/', this.getHeader)
				.then((response) => {
					this.report_url = `${url_original}?${response.data.params}`
					this.show_report = true
				})
				.catch((e) => {
					console.log(e)
				})
				.finally(() => {
					this.$q.loading.hide()
				})
		},

		showReportBI(report) {
			this.$refs.dropdownReport.hide()
			if (report.report_type === 3) {
				this.getAuthParamsData(report.url_original)
			} else {
				this.report_url = report.url
				this.show_report = true
			}
		},
	},
	created() {
		this.loadReports()
		this.loadPagination()
		this.fetchLists()
	},
}
</script>

<style lang="stylus" scoped></style>
